/**
 * オペレーターの詳細パネル(設計書 §5-3 一覧＋詳細型。2026-09-18)。
 * 基本情報・BB ID(複数を完全表示)・単価改定履歴(適用開始日・終了日・改定前後の金額)・交通費の改定履歴。
 * 表示だけで、編集は「編集」ボタンから既存の編集画面(OperatorEditModal)を開く(一覧での行内編集はしない)。
 */
import DetailPanel, { DetailList, DetailSection } from '@core/components/ui/DetailPanel';
import { addDays } from '@core/dashboard/calc';
import { fmtTs } from '@core/lib/auditLog';
import { toDateLabel, todayStr } from '@core/lib/date';
import {
  BUSINESS_CODES,
  BUSINESS_LABELS,
  INVOICE_TYPE_LABELS,
  OPERATOR_STATUS_LABELS,
  PAY_TYPE_CODES,
  PAY_TYPE_LABELS,
  WORK_STYLE_CODES,
  WORK_STYLE_CODE_LABELS,
  type BbAccount,
  type Company,
  type DateStr,
  type Operator,
  type OperatorStatus,
  type PayType,
} from '@core/lib/types';
import { formatCurrency, formatHourlyWage, formatUnitPrice, NO_DATA } from '@core/utils/formatters';
import { prefixKeyFor, styleKeyFor } from './bbNumbering';
import { DEFAULT_BB_NUMBERING, bbTypeCode } from '@core/lib/types';

export const STATUS_TONE: Record<OperatorStatus, string> = { active: 'tone-success', leave: 'tone-warning', retired: 'tone-neutral', hmt: 'tone-info', transfer: 'tone-info' };

export function StatusDot({ status }: { status: OperatorStatus }) {
  return <span className={`status-dot ${STATUS_TONE[status]}`}>{OPERATOR_STATUS_LABELS[status]}</span>;
}

/** 時給は ¥1,300/h、件数単価は ¥400/件 */
export function formatWage(payType: PayType, value: number | null | undefined): string {
  return payType === 'commission' ? formatUnitPrice(value) : formatHourlyWage(value);
}

type Revision = { from: DateStr; to: DateStr | null; before: number | null; after: number; reason: string; state: 'current' | 'planned' | 'ended' };
const STATE_LABELS: Record<Revision['state'], string> = { current: '適用中', planned: '予定', ended: '終了' };

/** 改定履歴を「適用期間(開始〜終了) ＋ 改定前 → 改定後」にする(新しい順) */
function toRevisions<T extends { effective_from: DateStr; reason?: string }>(history: T[], valueOf: (h: T) => number): Revision[] {
  const today = todayStr();
  const sorted = [...history].sort((a, b) => (a.effective_from < b.effective_from ? -1 : a.effective_from > b.effective_from ? 1 : 0));
  return sorted
    .map((h, i) => {
      const next = sorted[i + 1];
      const to = next ? addDays(next.effective_from, -1) : null;
      const state: Revision['state'] = h.effective_from > today ? 'planned' : to !== null && to < today ? 'ended' : 'current';
      return { from: h.effective_from, to, before: i > 0 ? valueOf(sorted[i - 1]!) : null, after: valueOf(h), reason: h.reason ?? '', state };
    })
    .reverse();
}

function RevisionList({ revisions, format, emptyText }: { revisions: Revision[]; format: (v: number) => string; emptyText: string }) {
  if (revisions.length === 0) return <p className="hint">{emptyText}</p>;
  return (
    <ul className="op-history">
      {revisions.map((r) => (
        <li key={r.from} className={`is-${r.state}`}>
          <span className="op-history-period">
            {toDateLabel(r.from)} 〜 {r.to ? toDateLabel(r.to) : ''}
            <span className="op-history-state">{STATE_LABELS[r.state]}</span>
          </span>
          <span className="op-history-amount">
            {r.before !== null && <span className="op-history-before">{format(r.before)} → </span>}
            <strong>{format(r.after)}</strong>
          </span>
          {r.reason && <span className="op-history-reason">{r.reason}</span>}
        </li>
      ))}
    </ul>
  );
}

export default function OperatorDetailPanel({ operator, companies, canEdit, onEdit, onClose }: { operator: Operator; companies: Company[]; canEdit: boolean; onEdit: () => void; onClose: () => void }) {
  const companyName = (id: string | null) => (id ? (companies.find((c) => c.id === id)?.name ?? id) : NO_DATA);
  // 古いデータはBB IDに出勤形態・報酬区分が無いので人の値を使う
  const styleOf = (a: BbAccount) => a.work_style ?? operator.work_style;
  const hasShinagawa = operator.bb_accounts.some((a) => styleOf(a) === 'shinagawa');
  const hasZaitaku = operator.bb_accounts.some((a) => styleOf(a) === 'zaitaku');

  return (
    <DetailPanel
      title={operator.name}
      subtitle={
        <>
          {operator.op_code} ・ <StatusDot status={operator.status} />
        </>
      }
      onClose={onClose}
      footer={
        canEdit && (
          <button type="button" className="ui-btn ui-btn-primary" onClick={onEdit}>
            編集
          </button>
        )
      }
    >
      <DetailSection title="基本情報">
        <DetailList
          items={[
            { label: '入社日', value: toDateLabel(operator.joined_at) },
            { label: '退職日', value: toDateLabel(operator.left_at) },
            // 交通費は出勤のみ(P-38)。出勤のBB IDが無い人は対象外(—)、出勤で 0円 は ¥0
            { label: '交通費(往復)', value: hasShinagawa ? formatCurrency(operator.commute_cost) : `${NO_DATA}(出勤なし)`, numeric: true },
            { label: 'インボイス控除', value: hasZaitaku ? (operator.invoice_type ? INVOICE_TYPE_LABELS[operator.invoice_type] : '未設定') : `${NO_DATA}(在宅なし)` },
            { label: 'PCレンタル', value: operator.pc_rental ? 'あり' : 'なし' },
            { label: '成績表の公開', value: operator.stats_visible ? '公開する' : '公開しない' },
            { label: '登録日', value: fmtTs(operator.created_at, false) },
            ...(operator.note ? [{ label: '備考', value: operator.note }] : []),
          ]}
        />
      </DetailSection>

      <DetailSection title={`BB ID(${operator.bb_accounts.length}件)`}>
        {operator.bb_accounts.length === 0 ? (
          <p className="hint">BB IDが登録されていません</p>
        ) : (
          operator.bb_accounts.map((a) => {
            const ws = styleOf(a);
            const pt = a.pay_type ?? operator.pay_type;
            return (
              <div key={a.bb_id} className="op-account">
                <div className="op-account-head">
                  <span className="op-account-id">{a.bb_id}</span>
                  <span className="hint">内線 {a.ext || NO_DATA}</span>
                  {!a.active && <span className="status-dot tone-neutral">{a.replaced_by ? '旧ID' : '無効'}</span>}
                  {a.replaced_by && <span className="hint">→ 新ID {a.replaced_by}</span>}
                  {a.replaced_from && <span className="hint">(旧ID {a.replaced_from})</span>}
                </div>
                <DetailList
                  items={[
                    { label: '出勤形態×報酬区分', value: `${WORK_STYLE_CODES[ws]} ${WORK_STYLE_CODE_LABELS[ws]} × ${PAY_TYPE_CODES[pt]} ${PAY_TYPE_LABELS[pt]}` },
                    { label: '架電内容', value: `${BUSINESS_CODES[a.business]} ${BUSINESS_LABELS[a.business]}` },
                    // 形態コード(D-112)。旧コード(SN・SR…)も併記する(2026-09-21 発注者指示)
                    { label: '形態コード', value: `${bbTypeCode(styleKeyFor(ws, pt), a.business)}(旧 ${DEFAULT_BB_NUMBERING.rules[prefixKeyFor(ws, pt, a.business)].prefix})` },
                    { label: '架電会社', value: companyName(a.company_id) },
                    { label: pt === 'commission' ? '現在の件数単価' : '現在の時給', value: a.wage_pending ? '未設定(仮登録)' : formatWage(pt, a.current_wage), numeric: !a.wage_pending },
                    { label: 'BlueBean内部ID', value: a.bb_internal_id || '未登録' },
                  ]}
                />
                <div className="op-history-block">
                  <h4 className="op-history-title">{pt === 'commission' ? '単価' : '時給'}の改定履歴</h4>
                  <RevisionList revisions={toRevisions(a.wage_history ?? [], (h) => h.wage)} format={(v) => formatWage(pt, v)} emptyText="改定履歴はありません(現在値のみ)" />
                </div>
              </div>
            );
          })
        )}
      </DetailSection>

      {hasShinagawa && (
        <DetailSection title="交通費の改定履歴">
          <RevisionList
            revisions={toRevisions(operator.commute_history ?? [], (h) => h.amount)}
            format={(v) => formatCurrency(v)}
            emptyText={`改定履歴はありません(現在値 ${formatCurrency(operator.commute_cost)})`}
          />
        </DetailSection>
      )}

      {(operator.extra_info ?? []).length > 0 && (
        <DetailSection title="その他情報">
          <DetailList items={(operator.extra_info ?? []).map((e) => ({ label: e.label || NO_DATA, value: e.value || NO_DATA }))} />
        </DetailSection>
      )}
    </DetailPanel>
  );
}
